import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Bell, Table2, Columns3 } from "lucide-react";
import { TrackingPanel } from "@/components/admin/TrackingPanel";
import { ChangePasswordPanel } from "@/components/admin/ChangePasswordPanel";
import { LeadKanban } from "@/components/admin/LeadKanban";
import { LeadTable } from "@/components/admin/LeadTable";
import { LeadDetail, STATUSES } from "@/components/admin/LeadDetail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("mbo_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("leads");
  const [statusFilter, setStatusFilter] = useState("");
  const [followUpOnly, setFollowUpOnly] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("mbo_admin_view") || "table");
  const [counts, setCounts] = useState({});

  useEffect(() => { localStorage.setItem("mbo_admin_view", view); }, [view]);

  const fetchLeads = useCallback(async (tk, status = "", followUp = false) => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (followUp) params.follow_up = "pending";
      const { data } = await axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${tk}` }, params });
      setLeads(data.leads);
      setCounts(data.counts || {});
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("mbo_admin_token");
        setToken("");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchLeads(token, statusFilter, followUpOnly);
  }, [token, statusFilter, followUpOnly, fetchLeads]);

  const onLeadUpdated = (updated) => {
    setSelected(updated);
    fetchLeads(token, statusFilter, followUpOnly);
  };

  const onLeadMoved = (lead, confirmed) => {
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, ...lead } : l)));
    if (confirmed) fetchLeads(token, statusFilter, followUpOnly);
  };

  const renderLeads = () => {
    if (loading) return <p className="font-sans-ui text-sm text-ink/50">Caricamento...</p>;
    if (view === "kanban") return <LeadKanban leads={leads} token={token} onOpen={setSelected} onMoved={onLeadMoved} />;
    if (leads.length === 0) {
      const suffix = statusFilter || followUpOnly ? "con questi filtri" : "ancora";
      return <p className="font-sans-ui text-sm text-ink/50" data-testid="leads-empty">Nessun lead {suffix}.</p>;
    }
    return <LeadTable leads={leads} onOpen={setSelected} />;
  };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("mbo_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Accesso non riuscito.");
    }
  };

  const logout = () => {
    localStorage.removeItem("mbo_admin_token");
    setToken("");
    setLeads([]);
  };

  const exportCsv = async () => {
    const { data } = await axios.get(`${API}/admin/leads/export`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "mbo_leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-5">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm bg-ivory p-10">
          <img src="/logo-mbo.png" alt="My Brand Online" className="h-5 w-auto mb-6" />
          <h1 className="font-display text-3xl text-ink mb-8">Area Riservata</h1>
          <label className="block font-sans-ui text-[10px] tracking-mega text-ink/50 mb-2">EMAIL</label>
          <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent border-b hairline py-3 mb-6 font-sans-ui text-sm focus:border-ink focus:outline-none" />
          <label className="block font-sans-ui text-[10px] tracking-mega text-ink/50 mb-2">PASSWORD</label>
          <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border-b hairline py-3 mb-8 font-sans-ui text-sm focus:border-ink focus:outline-none" />
          {error && <p className="text-destructive text-sm mb-4 font-sans-ui">{error}</p>}
          <button data-testid="admin-login-submit" type="submit" className="w-full bg-ink text-ivory py-4 text-[11px] tracking-mega font-semibold hover:bg-charcoal transition-colors">
            ACCEDI
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory">
      <header className="border-b hairline px-5 sm:px-8 h-16 flex items-center justify-between sticky top-0 bg-ivory/90 backdrop-blur z-10">
        <div className="flex items-center gap-8">
          <span className="flex items-center gap-3"><img src="/logo-mbo.png" alt="My Brand Online" className="h-4 w-auto" /><span className="font-sans-ui text-[10px] tracking-mega text-ink/50">ADMIN</span></span>
          <nav className="hidden sm:flex gap-6">
            {[["leads", "LEAD"], ["tracking", "TRACKING / META PIXEL"], ["account", "ACCOUNT"]].map(([k, label]) => (
              <button
                key={k}
                data-testid={`admin-tab-${k}`}
                onClick={() => setTab(k)}
                className={`font-sans-ui text-[10px] tracking-mega py-1 border-b-2 transition-colors ${
                  tab === k ? "text-ink border-ink" : "text-ink/45 border-transparent hover:text-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-4">
          {tab === "leads" && (
            <>
              <span className="font-sans-ui text-xs text-ink/50">{leads.length} lead</span>
              <button onClick={exportCsv} data-testid="admin-export-csv" className="bg-ink text-ivory px-5 py-2.5 text-[10px] tracking-mega font-medium hover:bg-charcoal transition-colors">
                EXPORT CSV
              </button>
            </>
          )}
          <button onClick={logout} className="text-[10px] tracking-mega text-ink/50 hover:text-ink transition-colors">ESCI</button>
        </div>
      </header>

      <nav className="sm:hidden flex gap-6 px-5 border-b hairline">
        {[["leads", "LEAD"], ["tracking", "TRACKING"], ["account", "ACCOUNT"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`font-sans-ui text-[10px] tracking-mega py-3 border-b-2 ${tab === k ? "text-ink border-ink" : "text-ink/45 border-transparent"}`}>
            {label}
          </button>
        ))}
      </nav>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10">
        {tab === "account" && (
          <ChangePasswordPanel token={token} onTokenRefresh={(t) => { localStorage.setItem("mbo_admin_token", t); setToken(t); }} />
        )}
        {tab === "tracking" && <TrackingPanel token={token} />}
        {tab === "leads" && (
          <>
            <div className="flex flex-wrap gap-2 mb-8" data-testid="lead-status-filters">
              <div className="inline-flex border hairline mr-2" data-testid="lead-view-toggle">
                {[["table", "TABELLA", Table2], ["kanban", "PIPELINE", Columns3]].map(([k, label, Icon]) => (
                  <button
                    key={k}
                    data-testid={`lead-view-${k}`}
                    onClick={() => { setView(k); if (k === "kanban") setStatusFilter(""); }}
                    className={`inline-flex items-center gap-2 font-sans-ui text-[10px] tracking-mega px-4 py-2.5 transition-colors ${
                      view === k ? "bg-ink text-ivory" : "text-ink/55 hover:text-ink"
                    }`}
                  >
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>
              {view === "table" && [{ key: "", label: "Tutti" }, ...STATUSES].map((s) => {
                const n = s.key ? counts[s.key] ?? 0 : STATUSES.reduce((a, x) => a + (counts[x.key] ?? 0), 0);
                return (
                  <button
                    key={s.key || "all"}
                    data-testid={`lead-filter-${s.key || "all"}`}
                    onClick={() => setStatusFilter(s.key)}
                    className={`font-sans-ui text-[10px] tracking-mega px-4 py-2.5 border transition-colors ${
                      statusFilter === s.key ? "bg-ink text-ivory border-ink" : "hairline text-ink/55 hover:border-ink hover:text-ink"
                    }`}
                  >
                    {s.label.toUpperCase()} <span className="opacity-60 ml-1">{n}</span>
                  </button>
                );
              })}
              <button
                data-testid="lead-filter-follow-up"
                onClick={() => setFollowUpOnly((v) => !v)}
                className={`ml-auto inline-flex items-center gap-2 font-sans-ui text-[10px] tracking-mega px-4 py-2.5 border transition-colors ${
                  followUpOnly ? "bg-goldtan text-ink border-goldtan" : "hairline text-ink/55 hover:border-ink hover:text-ink"
                }`}
              >
                <Bell size={12} /> DA RICHIAMARE <span className="opacity-60 ml-1">{counts.follow_up_pending ?? 0}</span>
              </button>
            </div>
            {renderLeads()}
          </>
        )}
      </div>

      {selected && (
        <LeadDetail lead={selected} token={token} onClose={() => setSelected(null)} onUpdated={onLeadUpdated} />
      )}
    </div>
  );
}
